import { ScheduleServiceDialog } from "@/components/site/ScheduleServiceDialog";
import { Link } from "@/i18n/Link";
import { ArrowRight, Wrench, ShieldCheck, Clock, Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageLayout } from "@/components/site/PageLayout";
import { Seo, breadcrumbJsonLd, organizationJsonLd, serviceJsonLd } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { SITE_PHONE_DISPLAY } from "@/routes-data";
import { useCopy } from "@/i18n/LocaleContext";

const COPY = {
  en: {
    breadcrumbs: { home: "Home", repair: "Axle Repair" },
    seo: {
      title: "Trailer Axle Repair in Elkhart, IN - Same-Day Service",
      desc: "Trailer axle repair in Elkhart, IN: bearing repacks, brake service, spindle and full axle replacement on light-duty trailers up to 10,000 lb. Schedule online.",
      serviceName: "Trailer Axle Repair & Service",
      serviceDesc: "Bearing repacks, brake service, axle and spindle replacement, and custom axle builds in Elkhart, IN."
    },
    hero: {
      label: "Trailer Repair",
      title: "Trailer service done right the first time.",
      text: (link1: React.ReactNode, link2: React.ReactNode) => <>Bearings, brakes, spindles, axles, suspension. We've been doing this for sixty-plus years combined and we don't guess. Trailer down or in a hurry? {link1} handles most jobs the day you drop off, and you can {link2} before you call.</>,
      link1: "Emergency & same-day trailer repair",
      link2: "see current axle replacement costs",
      cta: "Schedule online"
    },
    services: {
      title: "What we service",
      items: [
        { title: "Bearing repack & replacement", desc: "Inner and outer bearings, races, and seals - repacked or replaced with the right grease for the application." },
        { title: "Brake service", desc: "Electric brake magnets, shoes, drums, and full assembly swaps. Hydraulic drum and disc service." },
        { title: "Axle replacement", desc: "Bent or worn-out axle? We spec the right Dexter replacement - capacity, hub face, brake type, length - and swap it in our shop." },
        { title: "Spindle replacement", desc: "Cut-and-weld spindle replacement on Dexter and most major-brand axles." },
        { title: "Custom axle builds", desc: "Spec'd to your exact length, capacity, brake type, and hub configuration. Built locally." },
        { title: "Suspension upgrades", desc: "Springs, hangers, equalizers, shackles. Convert slipper to double-eye or upgrade for capacity." }
      ]
    },
    howItWorks: {
      title: "How it works",
      steps: [
        { n: 1, title: "Schedule", desc: "Book online or call. Tell us what's wrong - even if you're not sure." },
        { n: 2, title: "Diagnose", desc: "We assess the trailer in our service bay and give you a clear path forward and a fair price." },
        { n: 3, title: "Repair", desc: "Most repairs are completed the same day or next, with OEM-quality parts in stock." }
      ]
    },
    faqs: {
      title: "Frequently asked questions",
      intro: (link1: React.ReactNode, link2: React.ReactNode) => <>Need it done today? See {link1}. Budgeting a job? Our {link2} has current price ranges by axle type.</>,
      link1: "emergency & same-day trailer repair",
      link2: "2026 axle replacement cost guide",
      items: [
        { q: "Do you replace bent or damaged trailer axles?", a: "Yes. We don't straighten bent axles - once a tube has taken a set, it's compromised - but we spec and install OEM-quality Dexter replacements, often the same day. Bring the trailer in and we'll measure capacity, hub face, and brake type and quote a clean swap." },
        { q: "How often should I repack my trailer wheel bearings?", a: "We recommend repacking every 12,000 miles or once a year, whichever comes first. Boat-trailer bearings that are submerged in water should be repacked more frequently, typically once a season at minimum, plus an inspection any time you suspect water intrusion." },
        { q: "Do you replace trailer brakes?", a: "Yes - electric drum, hydraulic drum, and hydraulic disc. We service Dexter, Lippert, and most other major brake assemblies. Most magnet, shoe, and full-assembly swaps are same-day if the parts are in stock, which they usually are." },
        { q: "Do I need an appointment?", a: "For repair work, yes. Schedule online at axle.setmore.com or give us a call. For parts pickups, walk-ins are welcome during business hours." },
        { q: "Do you service commercial fleets?", a: "Yes. We work with local dealers, manufacturers, and fleets on volume axle and parts orders, and we keep dedicated stock for fleet maintenance schedules. Call the shop to set up an account." }
      ]
    },
    emergency: {
      title: "Stuck on the side of the road?",
      text: "Call us. If we can talk you through it, we will. If we can't, we'll get you scheduled fast."
    }
  },
  es: {
    breadcrumbs: { home: "Inicio", repair: "Reparación de ejes" },
    seo: {
      title: "Reparación de ejes de remolque en Elkhart, IN - Servicio el mismo día",
      desc: "Reparación de ejes de remolque en Elkhart, IN: reempaquetado de rodamientos, servicio de frenos, reemplazo de husillos y ejes completos en remolques de carga ligera hasta 10,000 lb. Programe en línea.",
      serviceName: "Reparación y servicio de ejes de remolque",
      serviceDesc: "Reempaquetado de rodamientos, servicio de frenos, reemplazo de ejes y husillos, y fabricación de ejes personalizados en Elkhart, IN."
    },
    hero: {
      label: "Reparación de remolques",
      title: "Servicio de remolques bien hecho a la primera.",
      text: (link1: React.ReactNode, link2: React.ReactNode) => <>Rodamientos, frenos, husillos, ejes, suspensión. Tenemos más de sesenta años de experiencia combinada y no adivinamos. ¿Remolque descompuesto o tiene prisa? {link1} resuelve la mayoría de los trabajos el día que lo deja, y puede {link2} antes de llamar.</>,
      link1: "Reparación de emergencia y el mismo día para remolques",
      link2: "ver los costos actuales de reemplazo de ejes",
      cta: "Programar en línea"
    },
    services: {
      title: "Lo que reparamos",
      items: [
        { title: "Reempaquetado y reemplazo de rodamientos", desc: "Rodamientos internos y externos, pistas y sellos: reempaquetados o reemplazados con la grasa adecuada para la aplicación." },
        { title: "Servicio de frenos", desc: "Imanes de frenos eléctricos, zapatas, tambores y cambios completos de ensamblaje. Servicio de frenos hidráulicos de tambor y disco." },
        { title: "Reemplazo de ejes", desc: "¿Eje doblado o desgastado? Especificamos el reemplazo Dexter correcto (capacidad, cara de la maza, tipo de freno, longitud) y lo cambiamos en nuestro taller." },
        { title: "Reemplazo de husillos", desc: "Reemplazo de husillos mediante corte y soldadura en ejes Dexter y la mayoría de las marcas principales." },
        { title: "Fabricación de ejes personalizados", desc: "Especificados según su longitud, capacidad, tipo de freno y configuración de maza exactos. Construidos localmente." },
        { title: "Mejoras de suspensión", desc: "Ballestas, soportes, ecualizadores, grilletes. Convertimos de tipo deslizante a doble ojo o mejoramos la capacidad." }
      ]
    },
    howItWorks: {
      title: "Cómo funciona",
      steps: [
        { n: 1, title: "Programar", desc: "Reserve en línea o llame. Díganos qué sucede, incluso si no está seguro." },
        { n: 2, title: "Diagnosticar", desc: "Evaluamos el remolque en nuestra bahía de servicio y le damos un camino claro a seguir y un precio justo." },
        { n: 3, title: "Reparar", desc: "La mayoría de las reparaciones se completan el mismo día o al siguiente, con piezas de calidad OEM en existencia." }
      ]
    },
    faqs: {
      title: "Preguntas frecuentes",
      intro: (link1: React.ReactNode, link2: React.ReactNode) => <>¿Lo necesita hoy? Vea {link1}. ¿Presupuestando un trabajo? Nuestra {link2} tiene rangos de precios actuales por tipo de eje.</>,
      link1: "reparación de emergencia y el mismo día para remolques",
      link2: "guía de costos de reemplazo de ejes 2026",
      items: [
        { q: "¿Reemplazan ejes de remolque doblados o dañados?", a: "Sí. No enderezamos ejes doblados (una vez que un tubo se ha deformado, está comprometido), pero especificamos e instalamos reemplazos Dexter de calidad OEM, a menudo el mismo día. Traiga el remolque y mediremos la capacidad, la cara de la maza y el tipo de freno para cotizar un cambio limpio." },
        { q: "¿Cada cuánto debo reempaquetar los rodamientos de las ruedas de mi remolque?", a: "Recomendamos reempaquetar cada 12,000 millas o una vez al año, lo que ocurra primero. Los rodamientos de remolques para botes sumergidos en agua deben reempaquetarse con más frecuencia, típicamente una vez por temporada como mínimo, además de una inspección cada vez que sospeche que ha entrado agua." },
        { q: "¿Reemplazan frenos de remolque?", a: "Sí: tambor eléctrico, tambor hidráulico y disco hidráulico. Damos servicio a Dexter, Lippert y a la mayoría de los ensamblajes de frenos importantes. La mayoría de los cambios de imán, zapata y ensamblaje completo son el mismo día si las piezas están en existencia, lo cual generalmente es así." },
        { q: "¿Necesito una cita?", a: "Para trabajos de reparación, sí. Programe en línea en axle.setmore.com o llámenos. Para recoger piezas, se aceptan visitas sin cita durante el horario laboral." },
        { q: "¿Dan servicio a flotas comerciales?", a: "Sí. Trabajamos con concesionarios locales, fabricantes y flotas en pedidos de ejes y piezas por volumen, y mantenemos stock dedicado para los programas de mantenimiento de flotas. Llame al taller para abrir una cuenta." }
      ]
    },
    emergency: {
      title: "¿Atrapado al costado del camino?",
      text: "Llámenos. Si podemos ayudarle a resolverlo por teléfono, lo haremos. Si no, le programaremos una cita rápidamente."
    }
  }
};

export default function AxleRepair() {
  const t = useCopy(COPY);
  const trail = [
    { name: t.breadcrumbs.home, path: "/" },
    { name: t.breadcrumbs.repair, path: "/services/axle-repair/" },
  ];
  return (
    <PageLayout>
      <Seo
        title={t.seo.title}
        description={t.seo.desc}
        path="/services/axle-repair/"
        jsonLd={[
          organizationJsonLd(),
          breadcrumbJsonLd(trail),
          serviceJsonLd({
            name: t.seo.serviceName,
            description: t.seo.serviceDesc,
            path: "/services/axle-repair/",
            serviceType: "Trailer Repair",
          }),
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: t.faqs.items.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          },
        ]}
      />
      <section className="bg-secondary text-secondary-foreground">
        <div className="container mx-auto px-4 py-14 md:py-20">
          <div className="mb-6 [&_a]:text-secondary-foreground/70 [&_a:hover]:text-white [&_span]:text-white">
            <Breadcrumbs trail={trail} />
          </div>
          <p className="text-xs md:text-sm uppercase tracking-[0.22em] text-white font-semibold mb-4">{t.hero.label}</p>
          <h1 className="text-3xl md:text-5xl font-bold tracking-tight max-w-3xl">
            {t.hero.title}
          </h1>
          <p className="mt-5 text-lg text-secondary-foreground/80 max-w-2xl leading-relaxed">
            {t.hero.text(
              <Link to="/services/emergency-same-day-trailer-repair/" className="text-white underline underline-offset-4 decoration-primary hover:decoration-white">{t.hero.link1}</Link>,
              <Link to="/trailer-axle-replacement-cost/" className="text-white underline underline-offset-4 decoration-primary hover:decoration-white">{t.hero.link2}</Link>
            )}
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <ScheduleServiceDialog>
              <Button size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                {t.hero.cta} <ArrowRight className="ml-1.5 h-4 w-4" />
              </Button>
            </ScheduleServiceDialog>
            <Button asChild size="lg" variant="outline" className="bg-transparent border-white/30 text-white hover:bg-white/10">
              <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
                <Phone className="mr-1.5 h-4 w-4" /> {SITE_PHONE_DISPLAY}
              </a>
            </Button>
          </div>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-10 max-w-2xl">{t.services.title}</h2>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {t.services.items.map((s) => (
            <article key={s.title} className="border border-border rounded-md p-5 bg-card">
              <Wrench className="h-5 w-5 text-primary mb-3" />
              <h3 className="font-semibold mb-2">{s.title}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{s.desc}</p>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-muted/40 border-y border-border">
        <div className="container mx-auto px-4 py-16">
          <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-10 max-w-2xl">{t.howItWorks.title}</h2>
          <ol className="grid md:grid-cols-3 gap-6">
            {t.howItWorks.steps.map((step) => (
              <li key={step.n} className="bg-card rounded-md border border-border p-6">
                <div className="text-primary text-3xl font-bold mb-2">0{step.n}</div>
                <div className="font-semibold mb-1">{step.title}</div>
                <p className="text-sm text-muted-foreground leading-relaxed">{step.desc}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="container mx-auto px-4 py-16 md:py-20 max-w-3xl">
        <h2 className="text-2xl md:text-3xl font-bold tracking-tight mb-8">{t.faqs.title}</h2>
        <p className="text-muted-foreground leading-relaxed mb-8">
          {t.faqs.intro(
            <Link to="/services/emergency-same-day-trailer-repair/" className="text-primary-strong hover:underline underline-offset-4">{t.faqs.link1}</Link>,
            <Link to="/trailer-axle-replacement-cost/" className="text-primary-strong hover:underline underline-offset-4">{t.faqs.link2}</Link>
          )}
        </p>
        <div className="divide-y divide-border border-y border-border">
          {t.faqs.items.map((f) => (
            <details key={f.q} className="group py-5">
              <summary className="cursor-pointer list-none flex justify-between items-start gap-4 font-medium">
                {f.q}
                <span className="text-primary text-xl leading-none mt-0.5 group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="mt-3 text-muted-foreground leading-relaxed">{f.a}</p>
            </details>
          ))}
        </div>
      </section>

      <section className="bg-primary-deep text-primary-foreground">
        <div className="container mx-auto px-4 py-12 flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
          <div className="flex items-start gap-4">
            <ShieldCheck className="h-8 w-8 mt-1 shrink-0" />
            <div>
              <h2 className="text-2xl md:text-3xl font-bold">{t.emergency.title}</h2>
              <p className="mt-1 text-primary-foreground">{t.emergency.text}</p>
            </div>
          </div>
          <Button asChild size="lg" className="bg-white text-primary-strong hover:bg-white/90 shrink-0">
            <a href={`tel:${SITE_PHONE_DISPLAY.replace(/\D/g, "")}`}>
              <Phone className="mr-1.5 h-4 w-4" /> {SITE_PHONE_DISPLAY}
            </a>
          </Button>
        </div>
      </section>
    </PageLayout>
  );
}
